import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADDRESS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import TagChips from './TagChips';

export const CountryForm = NiceModal.create((props) => {
	const { mode = 'add', modalName, row, setCountry = undefined } = props;

	const [tags, setTags] = useState([]);

	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [id, setId] = useState(null);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					shortCode: yup
						.string()
						.min(2)
						.max(2)
						.nullable()
						.required()
						.matches(/^(?=.*[A-Z])/, t('passwordError.upperCase')),
					hasState: yup.boolean().nullable().required(),
					hasNeighbourhood: yup.boolean().nullable().required(),
					hasShortAddress: yup.boolean().nullable().required(),
					zipCodeRequired: yup.boolean().nullable().required(),
					allowStateCustomValue: yup.boolean().nullable().required(),
					allowCityCustomValue: yup.boolean().nullable().required(),
					allowDistrictCustomValue: yup.boolean().nullable().required(),
					internationalDocRequirement: yup.boolean().nullable().required(),
					useComplexCityComponent: yup.boolean().nullable().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			shortCode: null,
			hasState: false,
			hasNeighbourhood: false,
			hasShortAddress: false,
			zipCodeRequired: false,
			allowStateCustomValue: true,
			allowCityCustomValue: true,
			allowDistrictCustomValue: true,
			internationalDocRequirement: false,
			useComplexCityComponent: false
		}
	});

	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const getCountryTags = async (id) => {
		await axios
			.post(ADDRESS.GET_COUNTRY_TAG_LIST, {
				parentID: id
			})
			.then((res) => {
				if (Array.isArray(res?.data?.data) && res?.data?.data?.length > 0) {
					setTags(res?.data?.data);
				} else {
					setTags([]);
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const onSubmit = useCallback(
		async (data) => {
			setIsLoading(true);

			await axios
				.post(ADDRESS.SAVE_COUNTRY, {
					...data,
					...((row?.id || id) && { id: row?.id || id })
				})
				.then((response) => {
					setId(response.data.id);
					setCountry && setCountry(response.data);
					if (!row?.id && !id) {
						showSuccess({
							message: 'Country meta added.'
						});
						if (response.data?.id) {
							handleCloseModal();
						}
					} else {
						showSuccess({
							message: 'Country meta updated'
						});
						dispatch(updateDynamicListData(DYNAMICLISTS.COUNTRIES));
						handleCloseModal();
					}
				})
				.catch((error) => {
					if (error?.response?.data?.code === 'error.AD002') {
						showError({
							message: t('error.AD002', {
								value: t('country')
							})
						});
					} else {
						showError({
							message: error?.response?.data?.code || error?.response?.data?.message
						});
						AppLogger.error(error);
					}
				});
			setIsLoading(false);
		},
		[row, tags, id]
	);

	const defaultColSpan = {
		md: 6
	};

	useEffect(() => {
		if (row?.id) {
			reset({
				name: row.name,
				shortCode: row.shortCode,
				hasState: row.hasState === 'yes',
				hasNeighbourhood: row.hasNeighbourhood === 'yes',
				hasShortAddress: row.hasShortAddress === 'yes',
				zipCodeRequired: row.zipCodeRequired === 'yes',
				allowStateCustomValue: row.allowStateCustomValue === 'yes',
				allowCityCustomValue: row.allowCityCustomValue === 'yes',
				allowDistrictCustomValue: row.allowDistrictCustomValue === 'yes',
				internationalDocRequirement: row.internationalDocRequirement === 'yes',
				useComplexCityComponent: row.useComplexCityComponent === 'yes'
			});
			getCountryTags(row.id);
		}
	}, [row]);

	return (
		<Modal fullwidth name={modalName} maxWidth="x-large" className="modal-country">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'add' ? 'addCountry' : mode === 'edit' ? 'editCountry' : 'viewCountry'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="name" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="name" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="shortCode" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="shortCode" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="hasState" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="hasState" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="hasNeighbourhood" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="hasNeighbourhood" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="hasShortAddress" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="hasShortAddress" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="zipCodeRequired" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="zipCodeRequired" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="allowStateCustomValue" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="allowStateCustomValue" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="allowCityCustomValue" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="allowCityCustomValue" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="allowDistrictCustomValue" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="allowDistrictCustomValue" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem
						label="internationalDocRequirement"
						colSpan={defaultColSpan}
						isLoading={isLoading}
						required>
						<SwitchInput name="internationalDocRequirement" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					<FormItem label="useComplexCityComponent" colSpan={defaultColSpan} isLoading={isLoading} required>
						<SwitchInput name="useComplexCityComponent" className="mt-3" disabled={mode === 'view'} />
					</FormItem>
					{mode !== 'add' && (
						<FormItem label="tags" childStyle={{ margin: 0 }} isLoading={isLoading}>
							<TagChips
								tags={tags}
								setTags={setTags}
								parentID={row?.id || id}
								type="country"
								disabled={mode === 'view'}
								isLoading={isLoading}
							/>
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						label={mode === 'add' ? 'add' : 'update'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit)}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default CountryForm;
